import { useMemo, useState, useEffect, useCallback } from 'react';
import { getVariables, type UseIssuesInput } from '@atlassian/jira-business-common';
import { useSorter } from '@atlassian/jira-business-filters';
import {
	getAGGCompliantData,
	useJqlPagination,
	useListRecovery,
	type RecoveryReturn,
} from '@atlassian/jira-business-list-handlers';
import { markViewMetric, REQUEST_END, REQUEST_START } from '@atlassian/jira-business-performance';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import type {
	JWMListViewItems,
	JWMListViewItemsVariables as ListVariables,
	JWMListViewItems as Items,
} from './__generated_apollo__/JWMListViewItems';
import type {
	JWMListViewItemsBackground_jira_list_JiraWorkManagementViewItemConnection_edges_node as BackgroundListNode,
	JWMListViewItemsBackground as BackgroundItems,
} from './__generated_apollo__/JWMListViewItemsBackground';
import type {
	ListViewItemEdges_edges as ListEdges,
	ListViewItemEdges_edges_node as ListNode,
} from './__generated_apollo__/ListViewItemEdges';
import { LIST_VIEW_QUERY, BACKGROUND_LIST_VIEW_QUERY } from './gql';
import type { ListViewIssue, ListViewIssuesReturn } from './types';
import { transformResult } from './utils';

type ListViewRecoverySettings = {
	isVisible: boolean;
	updateInterval?: number;
	isDisabled?: boolean;
};

/**
 * Reloads the whole list and replaces the main list after completed
 *
 * @deprecated use `ListViewRecovery` instead. Remove when cleaning up 'jwm.list.background-refetch-for-groups-and-children'
 * */
export const useListViewRecovery = (
	input: UseIssuesInput,
	settings: ListViewRecoverySettings,
): RecoveryReturn => {
	const cloudId = useCloudId();
	const variables = {
		...getVariables(input),
		cloudId,
	};

	return useListRecovery<ListVariables, Items, ListNode, BackgroundItems, BackgroundListNode>({
		settings,
		queryList: LIST_VIEW_QUERY,
		queryBackgroundList: BACKGROUND_LIST_VIEW_QUERY,
		variables,
		onError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: 'listRecoveryQuery',
					packageName: 'jiraBusinessList',
					teamName: 'keystone',
				},
				attributes: {
					message: 'Failed to fetch issues in background in list view',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		onBackgroundListError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: 'listRecoveryBackgroundQuery',
					packageName: 'jiraBusinessList',
					teamName: 'keystone',
				},
				attributes: {
					message: 'Failed to fetch more issues in background in list view',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
	});
};

const emptyArray: Array<never> = [];

// Loads the list and takes care of pagination
export const useListViewIssues = ({
	input,
	condition = true,
	onComplete,
}: {
	input: UseIssuesInput;
	condition?: boolean;
	onComplete?: (data: JWMListViewItems) => void;
}): ListViewIssuesReturn => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [hasLoadingStarted, setHasLoadingStarted] = useState(false);
	const cloudId = useCloudId();

	const onNextPageLoaded = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'table endReached');
	}, [createAnalyticsEvent]);

	const queryVariables = getVariables(input);

	const { data, loadFirstPage, hasNextPage, loading, error, loadNextPage, isLoadingNextPage } =
		useJqlPagination<JWMListViewItems, ListNode, ListVariables>({
			variables: {
				cloudId,
				...queryVariables,
			},
			query: LIST_VIEW_QUERY,
			onCompleted: (responseData) => {
				fireOperationalAnalytics(createAnalyticsEvent({}), 'listViewItemsQuery succeeded', {
					issuesCount: getAGGCompliantData(responseData, 'list')?.edges?.length,
				});
				markViewMetric('list', REQUEST_END);
				if (onComplete) onComplete(responseData);
			},
			onError: (apolloError) => {
				fireErrorAnalytics({
					meta: {
						id: 'listViewItemsQuery',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: {
						message: 'Failed to fetch issues in list view',
					},
					error: apolloError,
					sendToPrivacyUnsafeSplunk: true,
				});
				markViewMetric('list', REQUEST_END);
			},
			onPaginationError: (apolloError) => {
				fireErrorAnalytics({
					meta: {
						id: 'listViewPaginationQuery',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: {
						message: 'Failed to fetch more issues in list view',
					},
					error: apolloError,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
			onNextPageLoaded,
		});

	useEffect(() => {
		// only call once when condition === true
		if (!data && condition) {
			markViewMetric('list', REQUEST_START);
			loadFirstPage();
			setHasLoadingStarted(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [condition, data]);

	const { sort } = useSorter<ListViewIssue>();

	const _data = getAGGCompliantData(data, 'list');
	const edges = _data?.edges || emptyArray;
	const intl = useIntl();

	const result: ListViewIssue[] = useMemo(() => {
		if (Array.isArray(edges) && edges.length === 0) {
			return edges;
		}

		const cacheVariables = {
			jql: queryVariables.jql,
			withConfluenceLinks: queryVariables.withConfluenceLinks,
			includeDoneItems: queryVariables.includeDoneItems,
			isParentFieldVisible: queryVariables.isParentFieldVisible,
			isDevSummaryFieldVisible: queryVariables.isDevSummaryFieldVisible,
		};

		return sort(
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			transformResult(edges as ListEdges[], {
				cacheVariables,
				intl,
			}),
		);
	}, [
		edges,
		sort,
		queryVariables.jql,
		queryVariables.withConfluenceLinks,
		queryVariables.includeDoneItems,
		queryVariables.isParentFieldVisible,
		queryVariables.isDevSummaryFieldVisible,
		intl,
	]);

	return {
		data: result,
		// Make sure to return loading=true until items are loaded.
		loading: hasLoadingStarted ? loading : true,
		error,
		loadNextPage,
		hasNextPage,
		isLoadingNextPage,
	};
};
